import {
  Button, Card, DateInput, Radios, TextInput,
} from 'nhsuk-react-components';
import type { DateInputChangeEvent } from 'nhsuk-react-components';
import {
  ChangeEvent, useState,
} from 'react';
import { ZodError } from 'zod';
import useCampaign from 'hooks/useCampaign.ts';
import useReferenceData from 'hooks/useReferenceData.ts';
import {
  Iteration,
  IterationDetailsFormValues,
  iterationDetailsFormSchema,
} from 'schemas/Iteration.schema.ts';
import {
} from 'schemas/enums/iterations.ts';
import {
  LabelDate,
  YYYYMMDDToHumanReadable,
  YYYYMMDDToLabel,
  YYYYMMDDToUnixDate,
  labelToYYYYMMDD,
  validateDateLengths,
} from 'utils/formattingUtils.ts';
import 'components/forms/IterationDetails/IterationDetailsForm.scss';
import IterationPageHeader from 'components/IterationPageHeader';
import CampaignPageHeader from 'components/CampaignPageHeader';
import featureFlags from 'services/featureFlags';
import { CampaignTemplate } from 'schemas/CampaignTemplates.schema.ts';

interface IIterationDetailsFormProps {
  onSuccessfulSubmission: (
    iterationDetails: IterationDetailsFormValues,
  ) => void;
  isSubmitting?: boolean;
  iteration: Iteration;
  template?: CampaignTemplate;
}

function IterationDetailsForm({
  onSuccessfulSubmission,
  isSubmitting = false,
  iteration,
  template,
}: IIterationDetailsFormProps) {
  const { campaign } = useCampaign();

  const { referenceData } = useReferenceData();
  const { cohorts } = referenceData;

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const isNewIteration = Boolean(!iteration?.ID);

  const [formValues, setFormValues] = useState<IterationDetailsFormValues>({
    Name: iteration?.Name || '',
    IterationDate: iteration?.IterationDate || '',
    CommsType: iteration?.CommsType || 'I',
    ApprovalMinimum: iteration?.ApprovalMinimum || 0,
    ApprovalMaximum: iteration?.ApprovalMaximum || 0,
    Type: iteration?.Type || 'A',
    IterationCohorts: [],
    IterationRules: [],
  });
  const [dates, setDates] = useState<{ IterationDate: LabelDate }>({
    IterationDate: YYYYMMDDToLabel(iteration?.IterationDate) || {
      day: '',
      month: '',
      year: '',
    },
  });

  function clearErrorsForField(fieldName: string) {
    if (errors[fieldName]) {
      const clearedErrors = { ...errors };
      delete clearedErrors[fieldName];
      setErrors(clearedErrors);
    }
  }

  const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    clearErrorsForField(e.target.name);
    setFormValues({
      ...formValues,
      [e.target.name]: e.target.value,
    });
  };

  const handleDateChange = (
    e: ChangeEvent<HTMLInputElement> | DateInputChangeEvent,
  ) => {
    const value = (e?.target?.value ?? e) as LabelDate;
    clearErrorsForField('IterationDate');
    setDates({
      ...dates,
      IterationDate: value,
    });

    const errorMessage = validateDateLengths(value);
    if (errorMessage) {
      setErrors({
        ...errors,
        IterationDate: errorMessage,
      });
    } else {
      clearErrorsForField('IterationDate');
    }
  };

  const onSubmit = () => {
    try {
      setErrors({});

      const YYYYMMDDIterationDate = labelToYYYYMMDD(dates.IterationDate);
      const submittedFormValues = {
        ...formValues,
        IterationDate: YYYYMMDDIterationDate,
        IterationCohorts:
          isNewIteration && (template?.['IterationDetails.IterationCohorts.input.default'])
            ? (template['IterationDetails.IterationCohorts.input.default'].split(',') ?? [])
              .map((CohortLabel, Priority) => ({
                CohortLabel,
                Priority,
              }))
            : (iteration.IterationCohorts ?? []),
        IterationRules:
            isNewIteration
              ? [
                ...JSON.parse(template?.['IterationDetails.IterationRules.filters.input.default'] ?? '[]'),
                ...JSON.parse(template?.['IterationDetails.IterationRules.suppressions.input.default'] ?? '[]'),
                ...JSON.parse(template?.['IterationDetails.IterationRules.routing.input.default'] ?? '[]'),
              ]
              : (iteration.IterationRules ?? []),
      };

      setFormValues(submittedFormValues);

      iterationDetailsFormSchema(cohorts).parse(submittedFormValues);

      const isUnique = !campaign.CampaignConfig?.Iterations?.some(
        (item) => item.Name?.trim() === submittedFormValues.Name?.trim()
          && item.ID !== iteration.ID,
      );

      if (!isUnique) {
        setErrors({
          ...errors,
          Name: 'Iteration name must be unique',
        });
        return;
      }

      if (
        campaign.CampaignConfig.StartDate
        && campaign.CampaignConfig.EndDate
      ) {
        const unixCampaignStartDate = YYYYMMDDToUnixDate(
          campaign.CampaignConfig.StartDate,
        );
        const unixCampaignEndDate = YYYYMMDDToUnixDate(
          campaign.CampaignConfig.EndDate,
        );
        const unixIterationDate = YYYYMMDDToUnixDate(
          submittedFormValues.IterationDate,
        );

        if (
          unixIterationDate < unixCampaignStartDate
          || unixIterationDate > unixCampaignEndDate
        ) {
          setErrors({
            ...errors,
            IterationDate: `The iteration date must be between the campaign start date [${YYYYMMDDToHumanReadable(campaign.CampaignConfig.StartDate)}] and end date [${YYYYMMDDToHumanReadable(campaign.CampaignConfig.EndDate)}]. Dates outside this range are not allowed, as these were set when the campaign was created.`,
          });

          return;
        }
      }

      if (
        submittedFormValues.ApprovalMaximum
        < submittedFormValues.ApprovalMinimum
      ) {
        setErrors({
          ...errors,
          ApprovalMinimum:
            'Minimum approval number must be less than maximum approval number',
        });
        return;
      }

      onSuccessfulSubmission(submittedFormValues);
    } catch (e) {
      if (e instanceof ZodError) {
        setErrors(Object.fromEntries(
          e.issues.map(({ path, message }) => [path[0].toString(), message]),
        ));
      }
    }
  };

  return (
    <>
      {isNewIteration ? (
        <IterationPageHeader
          iteration={iteration}
          title="Set iteration details"
        />
      ) : (
        <CampaignPageHeader
          campaign={campaign}
          template={template}
          title="Set iteration details"
          showLink={false}
        />
      )}
      <Card>
        <>
          <h3>What type of iteration do you want to create?</h3>
          <p>
            An iteration is a set of rules that creates part of a campaign.
            Often, an iteration is for a particular age group or date. When an
            iteration is complete, a group of people are identified for a first
            communication or reminder communication.
          </p>
          {!featureFlags.dryRunEnabled && (
            <div className="form-field-group">
              <h4>Iteration type</h4>
              <Radios
                inline
                name="Type"
                onChange={(e) => {
                  setFormValues({
                    ...formValues,
                    Type: (e.target as HTMLInputElement).value as 'A' | 'M',
                  });
                }}
              >
                <Radios.Item value="A" checked={formValues.Type === 'A'}>
                  Automatic
                </Radios.Item>
                <Radios.Item value="M" checked={formValues.Type === 'M'}>
                  Manual
                </Radios.Item>
              </Radios>
            </div>
          )}
          <div className="form-field-group">
            <h4>Communication type</h4>
            <Radios
              inline
              name="CommsType"
              onChange={(e) => {
                setFormValues({
                  ...formValues,
                  CommsType: (e.target as HTMLInputElement).value as 'I' | 'R',
                });
              }}
            >
              <Radios.Item value="I" checked={formValues.CommsType === 'I'}>
                First communication
              </Radios.Item>
              <Radios.Item value="R" checked={formValues.CommsType === 'R'}>
                Reminder communication
              </Radios.Item>
            </Radios>
          </div>
          <div className="form-field-group">
            <h4>Choose an iteration name</h4>
            <TextInput
              id="iteration-name"
              label="Choose a name that describes the iteration and is easy to understand. You should refer to the iteration of people being contacted and include any other special criteria."
              hint="For example 'First invite 65+ in London and 75+ elsewhere'."
              name="Name"
              onChange={handleTextChange}
              value={formValues.Name}
              error={errors.Name}
            />
          </div>
          <h4>Planned run date</h4>
          <div className="form-field-group">
            <DateInput
              id="iteration-date"
              legend="This is the first date that the iteration will execute. If it&apos;s a repeating iteration, the subsequent executions will be based on this date."
              onChange={handleDateChange}
              value={dates.IterationDate}
              error={errors.IterationDate}
            />
          </div>
        </>
      </Card>
      <Button onClick={onSubmit} disabled={isSubmitting}>
        {isSubmitting ? 'Updating...' : 'Save and continue'}
      </Button>
    </>
  );
}

export default IterationDetailsForm;
